import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { money } from "./ownerCenterUi";

function directionFromTransaction(transaction) {
  if (transaction?.type) return transaction.type;
  if (transaction?.kind === "finance_income") return "income";
  if (transaction?.kind === "finance_expense") return "expense";
  return "";
}

function titleFromTransaction(transaction) {
  return transaction?.description || transaction?.title || transaction?.category || (directionFromTransaction(transaction) === "income" ? "Ingreso mensual" : "Pago mensual");
}

export default function OwnerRecurringFromTransactionDialog({ open, onClose, transaction = null, recurring = null, onSaved }) {
  const [mode, setMode] = React.useState("new");
  const [planId, setPlanId] = React.useState("");
  const [name, setName] = React.useState("");
  const [amountMode, setAmountMode] = React.useState("fixed");
  const [endDate, setEndDate] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const direction = directionFromTransaction(transaction);
  const currency = transaction?.currency || "ARS";
  const plans = (recurring?.plans || []).filter((plan) => plan.effectiveStatus !== "ended" && plan.direction === direction && plan.currency === currency);

  React.useEffect(() => {
    if (!open) return;
    setMode("new");
    setPlanId("");
    setName(titleFromTransaction(transaction));
    setAmountMode("fixed");
    setEndDate("");
    setError("");
  }, [open, transaction]);

  const submit = async () => {
    const transactionId = transaction?.entityId || transaction?.id || transaction?._id;
    if (!transactionId) return;
    setSaving(true);
    setError("");
    try {
      await ownerApi.linkFinanceTransactionRecurring(transactionId, mode === "existing"
        ? { planId }
        : { name, amountMode, endDate: endDate || null });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude convertir este movimiento en mensual."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000, pb: 1 }}>Convertir movimiento en mensual</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: .5 }}>
          <Box sx={{ p: 1.4, borderRadius: 2.4, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.04)" }}>
            <Typography sx={{ fontWeight: 950 }}>{titleFromTransaction(transaction)}</Typography>
            <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".82rem", mt: .2 }}>
              Este movimiento real queda como primer dato histórico. No se duplica ni cambia su origen.
            </Typography>
            {transaction?.amount !== null && transaction?.amount !== undefined ? (
              <Typography sx={{ fontWeight: 1000, mt: .7 }}>{money(transaction.amount, currency)}</Typography>
            ) : null}
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <FormControl fullWidth>
            <InputLabel>Qué querés hacer</InputLabel>
            <Select label="Qué querés hacer" value={mode} onChange={(e) => setMode(e.target.value)}>
              <MenuItem value="new">Crear una mensualidad nueva</MenuItem>
              {plans.length ? <MenuItem value="existing">Vincular a una mensualidad existente</MenuItem> : null}
            </Select>
          </FormControl>

          {mode === "existing" ? (
            <FormControl fullWidth>
              <InputLabel>Mensualidad</InputLabel>
              <Select label="Mensualidad" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                {plans.map((plan) => <MenuItem key={plan.id || plan._id} value={plan.id || plan._id}>{plan.name}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <>
              <TextField fullWidth label="Nombre" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej.: Alquiler" />
              <FormControl fullWidth>
                <InputLabel>Cómo se comporta el monto</InputLabel>
                <Select label="Cómo se comporta el monto" value={amountMode} onChange={(e) => setAmountMode(e.target.value)}>
                  <MenuItem value="fixed">Fijo · usar este monto como referencia mensual</MenuItem>
                  <MenuItem value="variable">Variable · aprender del promedio de pagos reales</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth type="date" label="Finaliza (opcional)" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </>
          )}

          <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".76rem" }}>
            WebProfit toma el día de este movimiento como día mensual inicial. Después podés registrar cada ocurrencia real y el promedio se actualiza solo.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.3 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
        <Button variant="contained" startIcon={<RepeatRoundedIcon />} onClick={submit} disabled={saving || (mode === "existing" ? !planId : !name.trim())} sx={{ textTransform: "none", fontWeight: 950 }}>
          {saving ? "Guardando…" : mode === "existing" ? "Vincular mensual" : "Activar mensualidad"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
